/* eslint-disable import/no-unused-modules */
import type {
    Proposal,
    ProductItem,
    PaymentItem,
    ProfessionalInfo,
    Contact,
    ProposalTemplate,
    InvoiceStatus,
    InvoicePaymentStatus,
    Currency,
} from '../type/types';
import { isBmPremiumCatalogueSku } from './is-bm-premium-catalogue-sku';
import { parseApiDateToLocal } from './utils';

/**
 * GraphQL Date scalars are ISO strings (UTC); normalise to unix ms in the browser-local zone for UI.
 */
function graphqlDateToTimestamp(value: unknown): number | null {
    if (value == null || value === '') {
        return null;
    }
    if (typeof value === 'number') {
        return value < 10000000000 ? value * 1000 : value;
    }
    const s = String(value).trim();
    const d = parseApiDateToLocal(s);

    return d.isValid() ? d.valueOf() : null;
}

const KNOWN_INVOICE_STATUSES: readonly InvoiceStatus[] = [
    'Draft',
    'Sent',
    'Expired',
    'Cancelled',
    'Accepted',
    'Issued',
    'PartialPaid',
    'Paid',
    'Overdue',
    'Refunded',
];

/**
 * Mirrors Invoice::isQuote() in PHP — quote phase follows status; when Cancelled, declined quotes
 * have QuoteDecisionDate set and voided invoices do not.
 */
export const isInvoiceQuote = (
    status: string | null | undefined,
    quoteDecisionDate?: string | null,
): boolean => {
    if (status == null || status === '') {
        return true;
    }
    if (status === 'Cancelled') {
        return (
            quoteDecisionDate != null && String(quoteDecisionDate).trim() !== ''
        );
    }
    if (['Draft', 'Sent', 'Expired'].includes(status)) {
        return true;
    }
    if (!KNOWN_INVOICE_STATUSES.includes(status as InvoiceStatus)) {
        return true;
    }
    return false;
};

/** Proposal UI document label: quote vs invoice, aligned with isInvoiceQuote. */
export const getDocType = (
    status: InvoiceStatus,
    quoteDecisionDate?: string | null,
): 'quote' | 'invoice' =>
    isInvoiceQuote(status, quoteDecisionDate) ? 'quote' : 'invoice';

// Define the GraphQL response types manually since there seems to be an issue with the generated types
interface CurrencyAmount {
    Amount: number;
    Currency: string;
}

interface Professional {
    ID: number;
    Title: string;
    SubTitle?: string | null;
    PostCode?: string | null;
    State?: string | null;
    BN?: string | null;
    Website?: string | null;
    Email?: string | null;
    Phone: string;
    PrimaryAddress?: string | null;
    City?: string | null;
}

interface Service {
    ID: string;
    SKU: string;
    Title: string;
    Description?: string | null;
    Quantity?: number | null;
    SortOrder?: number | null;
    UnitPrice: CurrencyAmount;
    DiscountAmountPerUnit?: number | null;
    DiscountApplied: boolean;
    Total: CurrencyAmount;
    Thumbnail?: string | null;
    TaxAmount?: number | null;
    TaxRate?: number | null;
}

interface InvoicePayment {
    ID: string;
    Amount: number | null;
    UUID?: string | null;
    Status: string | null;
    DueOption?: string | null;
    AmountOption?: string | null;
    AmountPercentage?: number | null;
    DueDaysAfterInvoiceSent?: number | null;
    DueDate: string | number | null;
    PaymentDate: string | number | null;
    SortOrder?: number | null;
}

interface FooterLink {
    title: string;
    url: string;
}

interface TokenizedField {
    Render?: string | null;
    Raw?: string | null;
}

interface Invoice {
    ID: number;
    UUID: string;
    Status: InvoiceStatus;
    InvoiceSentDate: string | null;
    ExpiryDate: string | null;
    SplitPayment: string;
    InvoiceKind?: string | null;
    Created: string;
    LastEdited: string;
    Title?: string | null;
    Description?: string | null;
    Scope?: TokenizedField | null;
    Notes?: TokenizedField | null;
    References?: TokenizedField | null;
    AccountDetails?: TokenizedField | null;
    ContactInfo?: TokenizedField | null;
    PaymentTerms?: TokenizedField | null;
    CreditCardTerms?: TokenizedField | null;
    BankTransferTerms?: TokenizedField | null;
    BusinessTermsVersions?: Array<{
        ID: number;
        Version: number;
        Title: string;
        URL: string;
        Terms?: TokenizedField | null;
        Category?: {
            ID: number;
            Title: string;
        } | null;
    }> | null;
    SubTotal: CurrencyAmount;
    Discounts: CurrencyAmount;
    Tax: CurrencyAmount;
    Total: CurrencyAmount;
    Professional?: Professional | null;
    // Direct contact fields from Invoice
    ContactID: string;
    ContactFirstName?: string | null;
    ContactSurname?: string | null;
    ContactPhone?: string | null;
    ContactEmail?: string | null;
    ContactAddress?: string | null;
    ContactPostCode?: string | null;
    ContactCity?: string | null;
    ContactCompanyName?: string | null;
    Services: Service[];
    Payments: InvoicePayment[];
    Headline?: string | null;
    SubHeadline?: string | null;
    FooterLinks?: FooterLink[] | null;
    StripeHostedInvoiceUrl?: string | null;
    StripeAccount?: string | null;
    PublishableKey?: string | null;
    HasStripePaymentMethod: boolean;
    /** Whether the invoice has a payment currently being processed */
    HasProcessingPayment: boolean;
    // Invoice-specific fields (only present when status is Accepted or later)
    HubspotID?: string | null;
    PONumber?: string | null;
    StartDate?: string | null;
    EndDate?: string | null;
    MembershipTerm?: string | null;
    HideMembershipTerm?: boolean | null;
    // Signature fields (set when quote is accepted)
    SignatureFullName?: string | null;
    SignatureEmail?: string | null;
    SignatureDataUrl?: string | null;
    QuoteDecisionDate?: string | null;
    // Deal owner from HubSpot
    DealOwner?: {
        ID: number;
        Name: string;
        Email?: string | null;
        Phone?: string | null;
        BookingUrl?: string | null;
        MemberImage?: { Avatar?: string | null } | null;
        MeetingImage?: { Avatar?: string | null } | null;
    } | null;
}

/**
 * Maps GraphQL Invoice data to Proposal interface
 * @param invoice - The invoice data from GraphQL query
 * @returns Proposal object that matches the expected interface
 */
export const mapInvoiceToProposal = (invoice: Invoice): Proposal => {
    if (!invoice) {
        throw new Error('Invalid invoice data');
    }

    const currency = (invoice.Total?.Currency as Currency) || 'NZD';

    // Map professional info
    const professionalInfo: ProfessionalInfo = {
        title: invoice.Professional?.Title || '',
        subTitle: invoice.Professional?.SubTitle || '',
        address: invoice.Professional?.PrimaryAddress || '',
        city: invoice.Professional?.City || '',
        postCode: invoice.Professional?.PostCode || '',
        state: invoice.Professional?.State || '',
        bn: invoice.Professional?.BN || '',
    };

    // Map contact info from direct contact fields
    const contact: Contact = {
        id: parseInt(invoice.ContactID) || 0,
        fullName:
            `${invoice.ContactFirstName || ''} ${invoice.ContactSurname || ''}`.trim(),
        email: invoice.ContactEmail || '',
        firstName: invoice.ContactFirstName || '',
        lastName: invoice.ContactSurname || '',
        companyName: invoice.ContactCompanyName || '',
        phone: invoice.ContactPhone || '',
        address: invoice.ContactAddress || '',
        postCode: invoice.ContactPostCode || '',
        city: invoice.ContactCity || '',
        hasStripePaymentMethod: false, // Not available in direct contact fields
    };

    // Map proposal template
    const proposalTemplate: ProposalTemplate = {
        headline: invoice.Headline || invoice.Title || 'Proposal', // Use GraphQL headline, title, or default
        subHeadline: invoice.SubHeadline || invoice.Description || '', // Use GraphQL subheadline, description, or default
        footerLinks: [],
        note: invoice.Notes?.Render || '', // Map notes if available
        contact: contact,
        contactInfo: invoice.ContactInfo?.Render || undefined,
        accountDetails: invoice.AccountDetails?.Render || undefined,
        scope: invoice.Scope?.Render || undefined,
        paymentTerms: invoice.PaymentTerms?.Render || undefined,
        creditCardTerms: invoice.CreditCardTerms?.Render || undefined,
        bankTransferTerms: invoice.BankTransferTerms?.Render || undefined,
        references: invoice.References?.Render || undefined,
        businessTerms:
            invoice.BusinessTermsVersions?.map((version) => ({
                title: version.Title || version.Category?.Title || '',
                body: version.Terms?.Render || '',
                url: version.URL,
            })) || [],
    };

    // Line items: preserve GraphQL array order (backend uses InvoiceServiceItem::$default_sort).
    const membershipKind = invoice.InvoiceKind === 'membership';
    const rawServices = invoice.Services ?? [];
    const filteredServices = membershipKind
        ? rawServices.filter((service) => !isBmPremiumCatalogueSku(service.SKU))
        : rawServices;
    const excludedPremiumLines =
        membershipKind && filteredServices.length < rawServices.length;

    const productItems: ProductItem[] = filteredServices.map(
        (service: Service) => ({
            id: Number(service.ID),
            name: service.Title,
            description: service.Description || '',
            imageUrl: service.Thumbnail || '', // Use thumbnail from GraphQL response
            quantity: service.Quantity || 0,
            unitPrice: service.UnitPrice?.Amount || 0,
            discountedUnitPrice: Math.max(
                0,
                (service.UnitPrice?.Amount || 0) -
                    (service.DiscountAmountPerUnit ?? 0),
            ),
            discountAmountPerUnit: service.DiscountAmountPerUnit ?? 0,
            discountApplied: service.DiscountApplied,
            taxRate: service.TaxRate || 0,
            totalPrice: (service.Total?.Amount || 0) - (service.TaxAmount || 0),
            totalPriceAfterTax: service.Total?.Amount || 0,
            currency,
        }),
    );

    // Payments: preserve GraphQL array order (InvoicePayment + SortableObjectExtension default sort).
    const paymentItems: PaymentItem[] = (invoice.Payments ?? []).map(
        (payment: InvoicePayment, idx: number) => ({
            id: Number(payment.ID),
            name: `Payment ${idx + 1}`, // Default name since not available
            status: mapPaymentStatus(payment.Status),
            dueDate: graphqlDateToTimestamp(payment.DueDate),
            paymentDate: graphqlDateToTimestamp(payment.PaymentDate),
            amount: payment.Amount || 0,
            currency,
        }),
    );

    // Map summaries from scope field
    const summaries: string[] = invoice.Scope?.Render
        ? invoice.Scope.Render.split('\n')
        : [];

    const docType = getDocType(invoice.Status, invoice.QuoteDecisionDate);

    // Map financial data: membership invoices omit BM premium catalogue lines from proposal totals when present.
    let subtotal = invoice.SubTotal?.Amount || 0;
    let discounts = invoice.Discounts?.Amount || 0;
    let tax = invoice.Tax?.Amount || 0;
    let total = invoice.Total?.Amount || 0;
    if (excludedPremiumLines) {
        subtotal = productItems.reduce((sum, p) => sum + p.totalPrice, 0);
        tax = productItems.reduce(
            (sum, p) => sum + (p.totalPriceAfterTax - p.totalPrice),
            0,
        );
        total = productItems.reduce((sum, p) => sum + p.totalPriceAfterTax, 0);
    }
    const isSplitPayment = paymentItems.length > 1;

    return {
        id: invoice.ID,
        urlSegment: invoice.UUID,
        uuid: invoice.UUID,
        docType,
        isSplitPayment,
        professionalInfo,
        proposalTemplate,
        productItems,
        paymentItems,
        status: invoice.Status || 'Draft',
        currency,
        discounts,
        subtotal,
        tax,
        total,
        summaries,
        customer: contact,
        invoiceSentDate: invoice.InvoiceSentDate
            ? new Date(invoice.InvoiceSentDate).getTime()
            : null,
        expiryDate: invoice.ExpiryDate
            ? new Date(invoice.ExpiryDate).getTime()
            : null,
        createdAt: new Date(invoice.Created).getTime(),
        updatedAt: new Date(invoice.LastEdited).getTime(),
        stripeHostedInvoiceUrl: invoice.StripeHostedInvoiceUrl || null,
        stripeAccount: invoice.StripeAccount || null,
        publishableKey: invoice.PublishableKey || null,
        hasStripePaymentMethod: invoice.HasStripePaymentMethod,
        hasProcessingPayment: invoice.HasProcessingPayment,
        poNumber: invoice.PONumber || undefined,
        invoiceNumber: invoice.HubspotID || undefined,
        startDate: invoice.StartDate || undefined,
        endDate: invoice.EndDate || undefined,
        membershipTerm: invoice.MembershipTerm ?? '',
        hideMembershipTerm: invoice.HideMembershipTerm ?? false,
        signature:
            invoice.SignatureFullName || invoice.SignatureDataUrl
                ? {
                      fullName: invoice.SignatureFullName || '',
                      email: invoice.SignatureEmail || '',
                      dataUrl: invoice.SignatureDataUrl || '',
                      signedDate: invoice.QuoteDecisionDate || '',
                  }
                : undefined,
        dealOwner: invoice.DealOwner
            ? {
                  id: String(invoice.DealOwner.ID),
                  name: invoice.DealOwner.Name,
                  email: invoice.DealOwner.Email || undefined,
                  phone: invoice.DealOwner.Phone || undefined,
                  bookingUrl: invoice.DealOwner.BookingUrl || undefined,
                  avatarUrl:
                      invoice.DealOwner.MeetingImage?.Avatar ??
                      invoice.DealOwner.MemberImage?.Avatar ??
                      undefined,
              }
            : undefined,
    };
};

/**
 * Maps payment status from GraphQL to component interface
 * @param status - Payment status from GraphQL
 * @returns Mapped payment status
 */
const mapPaymentStatus = (
    status: string | null | undefined,
): InvoicePaymentStatus => {
    if (!status) return 'Canceled';

    switch (status.toLowerCase()) {
        case 'fullypaid':
            return 'FullyPaid';
        case 'pending':
            return 'Upcoming';
        case 'overdue':
            return 'Overdue';
        case 'canceled':
            return 'Canceled';
        default:
            return 'Upcoming';
    }
};
